/**
 * A PROVA DE VIDA DAS TELAS (20/10/2026) — quando dizer "subiu" (`notifyAppReady`) ao atualizador.
 *
 * O plugin desfaz sozinho um pacote novo que não diz "subiu" a tempo (`appReadyTimeout`, 20 s em capacitor.config.ts).
 * Antes, o "subiu" era dito assim que a primeira tela montava — e o pacote 202610201338-28d0225 de hoje montava a lista,
 * dizia "subiu", e tinha o ATUALIZADOR travado (o canal não era lido): não era desfeito, e o aparelho não saía dele sozinho.
 *
 * Agora o "subiu" só é dito depois de o atualizador provar que funciona:
 *   1. ler o canal guardado (`lerCanal`) — é onde o 28d0225 travava;
 *   2. completar uma consulta ao servidor: QUALQUER resposta (200, 404, 500) ou um ERRO DE REDE (sem internet, a consulta
 *      falha logo) ou o teto da consulta (`TETO_DA_CONSULTA_MS`, rede lenta) — todos provam que o código chegou até a rede.
 * O que não chega até aí no prazo (o canal que nunca é lido, um erro de JavaScript antes da consulta) não diz "subiu", e o
 * plugin volta ao pacote anterior na abertura seguinte.
 *
 * SEM INTERNET: a consulta falha na hora (erro de rede), o "subiu" é dito, e nada é desfeito. E, mesmo que não fosse, o
 * plugin só confere o "subiu" na PRIMEIRA abertura de um pacote recém-aplicado (`checkRevert`: as telas que vieram com o
 * aplicativo e um pacote já confirmado nunca são desfeitos). A prova de vida não depende da página: roda também na página
 * da prova — e lá só consulta (um GET pequeno), nunca baixa nem troca telas (isso continua fora da prova).
 */
export const TETO_DA_CONSULTA_MS = 12_000;

export interface DepsDaProvaDeVida {
  lerCanal: () => Promise<string>;
  /** A consulta ao servidor; resolve com o status ou rejeita (erro de rede, teto). Qualquer desfecho conta. */
  consultar: (canal: string, sinal: AbortSignal) => Promise<unknown>;
  notificar: () => Promise<unknown>;
  tetoDaConsultaMs?: number;
}

/** Devolve o que aconteceu: "subiu" (notificou) ou o motivo de não ter notificado (só para o teste e o registro). */
export async function provaDeVida(d: DepsDaProvaDeVida): Promise<'subiu' | 'erro'> {
  try {
    const canal = await d.lerCanal(); // se travar aqui, nunca notifica — é o que o plugin precisa para desfazer
    const ctrl = new AbortController();
    let teto: ReturnType<typeof setTimeout> | undefined;
    const noTeto = new Promise<void>((r) => { teto = setTimeout(() => { ctrl.abort(); r(); }, d.tetoDaConsultaMs ?? TETO_DA_CONSULTA_MS); });
    try {
      // Resposta OU erro de rede OU o teto (corre contra a consulta, mesmo que ela ignore o cancelamento): chegou à rede.
      await Promise.race([d.consultar(canal, ctrl.signal).catch(() => undefined), noTeto]);
    } finally { clearTimeout(teto); }
    await d.notificar();
    return 'subiu';
  } catch {
    return 'erro';
  }
}
